/**
 * ACT2-690: wires {@link ConnectWarehouseCard} to the same two ways Settings → Warehouses
 * connects a warehouse — adopt a RudderStack account in one click, or create a connection in the Add
 * warehouse drawer — and reports back once either produced a connection. The accounts are the page's own
 * list (`useWarehouseAccountList`): in a sibling workspace (ACT2-1124) they include the accounts of the
 * readable workspaces, whose Connect copies the account first.
 */
import type { AnalyticsEventProperties } from '@server/analytics-events';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { Callout } from '@/_shared/components/ui/callout';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { CopyAccountDialog } from '@/settings/components/accountCopies/CopyAccountDialog';
import { ReadableWorkspacesNotice } from '@/settings/components/accountCopies/ReadableWorkspacesNotice';
import { useWarehousePermission } from '@/settings/components/warehouseAccount/accountCreatePermission';
import { AddWarehouseAccountDrawer } from '@/settings/components/warehouseAccount/addAccount/AddWarehouseAccountDrawer';
import { PermissionAction } from '@/settings/components/warehouseAccount/PermissionNotice';
import { useWarehouseAccountList } from '@/settings/components/warehouseAccount/useWarehouseAccountList';

import type { HeldMessage } from '../../hooks/useWarehouseGate';
import { ConnectWarehouseCard } from './ConnectWarehouseCard';

type PropertyPromptOutcome = AnalyticsEventProperties['warehousePromptResolved']['promptOutcome'];

interface ConnectWarehousePromptProps {
  held: HeldMessage;
  /** A connection now exists — send the held question. */
  onConnected: () => void;
  onSendAnyway: () => void;
  onDismiss: () => void;
}

export function ConnectWarehousePrompt({
  onConnected,
  onSendAnyway,
  onDismiss,
  ...rest
}: ConnectWarehousePromptProps): React.ReactElement | null {
  const { wp, workspace } = useWorkspace();
  // ACT2-1276: RudderStack's permission, not the Lookout role, decides who can connect a warehouse.
  const permission = useWarehousePermission();
  const orpc = useOrpc();
  // Several paths can report the same connection; the card closes, and is measured, once.
  const resolved = useRef(false);
  const resolve = (promptOutcome: PropertyPromptOutcome): boolean => {
    if (resolved.current) return false;
    resolved.current = true;
    trackClientEvent(ANALYTICS_EVENTS.warehousePromptResolved, { promptOutcome, role: workspace.role });
    return true;
  };
  const connectedOnce = (outcome: ConnectOutcome): void => {
    if (resolve(outcome)) onConnected();
  };
  // A connection made outside chat, such as in Settings in another tab, also sends the question. This is
  // the gate's own query, so it adds no request.
  const { data } = useQuery(orpc.settings.warehouses.listAccounts.queryOptions());
  const hasWarehouse = (data?.accounts.length ?? 0) > 0;
  // Not while the drawer is open: it can hold a notice about the connection it just made, and reports
  // that connection itself once the admin is done with it.
  const [addOpen, setAddOpen] = useState(false);
  // Closing the drawer with X or Escape over its notice skips its onConnected; the connection is still
  // the one it made.
  const drawerOpened = useRef(false);
  const openDrawer = (open: boolean): void => {
    if (open) drawerOpened.current = true;
    setAddOpen(open);
  };
  useEffect(() => {
    if (hasWarehouse && !addOpen) connectedOnce(drawerOpened.current ? 'created' : 'connected_elsewhere');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connectedOnce is recreated every render and guarded by a ref
  }, [hasWarehouse, addOpen]);

  const props: AdminPromptProps = {
    ...rest,
    onConnected: connectedOnce,
    onSendAnyway: () => {
      resolve('sent_anyway');
      onSendAnyway();
    },
    onDismiss: () => {
      resolve('dismissed');
      onDismiss();
    },
  };
  const blockedAction =
    permission.reauthRequired || permission.unknown ? <PermissionAction permission={permission} /> : null;
  // An open drawer stays mounted when a refresh blocks the user, so the wizard and what they typed survive;
  // the drawer's own requests are refused on the server.
  if (permission.blocked === null || addOpen)
    return (
      <AdminConnectWarehousePrompt
        {...props}
        addOpen={addOpen}
        setAddOpen={openDrawer}
        blockedReason={permission.blocked}
        blockedAction={blockedAction}
      />
    );
  // A user RudderStack does not allow cannot connect, so they get the card without the account requests.
  // While the answer loads, the card still shows the held question, Send anyway and Dismiss.
  return (
    <ConnectWarehouseCard
      blockedReason={permission.blocked}
      blockedAction={blockedAction}
      heldContent={props.held.content}
      accounts={[]}
      unconnectableCount={0}
      isLoading={permission.loading}
      unavailable={false}
      canConnect={false}
      anyConnecting={false}
      settingsHref={wp('/settings/warehouses')}
      onConnect={() => undefined}
      onCreateNew={() => undefined}
      onSendAnyway={props.onSendAnyway}
      onDismiss={props.onDismiss}
    />
  );
}

type ConnectOutcome = Extract<PropertyPromptOutcome, 'connected' | 'created' | 'connected_elsewhere'>;

interface AdminPromptProps extends Omit<ConnectWarehousePromptProps, 'onConnected'> {
  onConnected: (outcome: ConnectOutcome) => void;
}

interface DrawerState {
  addOpen: boolean;
  setAddOpen: (open: boolean) => void;
  /** Why the user is now blocked, while the drawer stays open; null when RudderStack allows them. */
  blockedReason: string | null;
  blockedAction: React.ReactNode;
}

function AdminConnectWarehousePrompt({
  held,
  onConnected,
  onSendAnyway,
  onDismiss,
  addOpen,
  setAddOpen,
  blockedReason,
  blockedAction,
}: AdminPromptProps & DrawerState): React.ReactElement {
  const { wp } = useWorkspace();
  // Hook-level onSuccess still fires after unmount; without this, leaving for Settings mid-connect would
  // send the held question and navigate away from wherever the user went.
  // Set in the effect body, not only at init: StrictMode's dev replay runs the cleanup once, and a ref
  // left false there would swallow every connect.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const connected = (outcome: ConnectOutcome): void => {
    if (mounted.current) onConnected(outcome);
  };

  // The Warehouses page's own list, so the two cannot drift apart. A local save reports through the hook;
  // every other way the drawer creates a connection reports through its onConnected, after any notice the
  // admin has to read.
  const list = useWarehouseAccountList({
    onAdoptedAsNew: () => connected('connected'),
    onConnectionSaved: () => {
      setAddOpen(false);
      connected('created');
    },
  });
  const { adopt, organization } = list;
  // A plain request failure (a 500, a timeout) leaves `unavailable` unset but still has a problem.
  const ownUnavailable = adopt.unavailable !== undefined || adopt.unavailableProblem !== undefined;
  // A failed read of this workspace's own accounts still lists the readable workspaces' accounts, with the failure as
  // a notice above them. Only a list with nothing to connect is unavailable.
  const unavailable = ownUnavailable && list.connectable.length === 0;
  const notice = list.showReadableWorkspacesNotice ? (
    <ReadableWorkspacesNotice />
  ) : organization.unavailable !== null ? (
    <Callout variant="warning">
      Could not read the accounts of your organization’s other workspaces just now, so only this workspace’s accounts
      are listed.
    </Callout>
  ) : ownUnavailable && !unavailable ? (
    adopt.unavailableProblem ? (
      <WarehouseProblemCallout problem={adopt.unavailableProblem} />
    ) : (
      <Callout variant="warning">Your RudderStack warehouse accounts couldn’t be loaded.</Callout>
    )
  ) : null;

  return (
    <>
      <ConnectWarehouseCard
        heldContent={held.content}
        accounts={list.connectable}
        unconnectableCount={list.unconnectableCount}
        // Available is worked out from every list, as the Warehouses page waits for them.
        isLoading={list.isLoading}
        unavailable={unavailable}
        unavailableProblem={adopt.unavailableProblem}
        canConnect={blockedReason === null}
        blockedReason={blockedReason}
        blockedAction={blockedAction}
        anyConnecting={list.anyConnecting}
        notice={notice}
        settingsHref={wp('/settings/warehouses')}
        onConnect={list.connect}
        onCreateNew={() => setAddOpen(true)}
        onSendAnyway={onSendAnyway}
        onDismiss={onDismiss}
      />
      <CopyAccountDialog organization={organization} />
      <AddWarehouseAccountDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        onConnected={() => connected('created')}
        hook={list.connections}
      />
    </>
  );
}
